import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageSquareHeart, Loader2, Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WORD_LIMIT = 100;

/**
 * FeedbackDialog — top-center modal for anonymous + signed-in feedback.
 * Auto-listens for the global `buddy:open-feedback` event so any footer link
 * across the app can just `window.dispatchEvent(new CustomEvent("buddy:open-feedback"))`.
 */
export default function FeedbackDialog({ open: openProp, onClose }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const close = () => { setInternalOpen(false); onClose?.(); };
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const wordCount = message.trim() ? message.trim().split(/\s+/).length : 0;
  const overLimit = wordCount > WORD_LIMIT;

  useEffect(() => {
    const handler = () => { setInternalOpen(true); setMessage(""); setStatus("idle"); setErrorMsg(""); };
    window.addEventListener("buddy:open-feedback", handler);
    return () => window.removeEventListener("buddy:open-feedback", handler);
  }, []);

  const handleSubmit = async () => {
    if (!message.trim() || overLimit || status === "sending") return;
    setStatus("sending"); setErrorMsg("");
    try {
      const token = localStorage.getItem("buddy_access_token");
      await axios.post(`${BACKEND_URL}/api/feedback`,
        { message: message.trim() },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      setStatus("success");
      setTimeout(() => { setMessage(""); setStatus("idle"); close(); }, 1500);
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.response?.data?.detail || "Couldn't send. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998]"
            style={{ background: "rgba(4, 32, 22, 0.55)", backdropFilter: "blur(4px)" }}
            onClick={close}
            data-testid="feedback-backdrop"
          />
          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, y: -20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -20, x: "-50%" }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            className="fixed left-1/2 top-16 z-[9999] w-full max-w-lg px-4"
            data-testid="feedback-dialog"
          >
            <div
              className="rounded-2xl shadow-2xl overflow-hidden"
              style={{
                background: "var(--surface-white)",
                border: "2px solid var(--rolex-green)",
                boxShadow: "0 20px 60px rgba(0,96,57,0.25), 0 0 0 4px rgba(0,96,57,0.08)",
              }}
            >
              {/* Header */}
              <div className="px-6 pt-6 pb-4 flex items-start gap-3" style={{ borderBottom: "1px solid rgba(0,96,57,0.15)" }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ background: "var(--rolex-gold-light)", color: "var(--rolex-green)" }}>
                  <MessageSquareHeart size={20} />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-2xl font-bold" style={{ color: "var(--rolex-green)" }} data-testid="feedback-heading">
                    Feedback
                  </h2>
                  <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }} data-testid="feedback-tagline">
                    We&apos;re listening, improving, and growing with you.
                  </p>
                </div>
                <button onClick={close} className="p-1 rounded-full hover:bg-black/5 transition-colors" data-testid="feedback-close">
                  <X size={18} style={{ color: "var(--text-light)" }} />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5">
                {status === "success" ? (
                  <div className="flex flex-col items-center gap-3 py-6" data-testid="feedback-success">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "var(--rolex-green)", color: "#fff" }}>
                      <Check size={28} />
                    </div>
                    <p className="text-base font-semibold" style={{ color: "var(--rolex-green)" }}>Thank you!</p>
                    <p className="text-sm" style={{ color: "var(--text-muted)" }}>Your feedback just landed on our desk.</p>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us what's working, what's not, or what you'd love to see next…"
                      className="w-full rounded-lg p-3 text-sm resize-none focus:outline-none transition-colors"
                      style={{
                        minHeight: "140px",
                        background: "var(--surface-warm)",
                        border: `1.5px solid ${overLimit ? "var(--error)" : "rgba(0,96,57,0.2)"}`,
                        color: "var(--text-primary)",
                      }}
                      disabled={status === "sending"}
                      data-testid="feedback-textarea"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs" style={{ color: overLimit ? "var(--error)" : "var(--text-light)" }} data-testid="feedback-word-count">
                        {wordCount} / {WORD_LIMIT} words
                      </span>
                      {errorMsg && <span className="text-xs" style={{ color: "var(--error)" }}>{errorMsg}</span>}
                    </div>
                    <div className="flex justify-end gap-2 mt-5">
                      <button
                        onClick={close}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        style={{ background: "transparent", color: "var(--text-muted)" }}
                        data-testid="feedback-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={!message.trim() || overLimit || status === "sending"}
                        className="px-5 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: "var(--rolex-green)", color: "#fff" }}
                        data-testid="feedback-submit"
                      >
                        {status === "sending" ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Submit"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
